/**
 * GA4 recommended ecommerce events via dataLayer (GTM / gtag).
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

function clearEcommerce() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
}

/**
 * @param {string} eventName
 * @param {object} params
 * @param {string} [params.currency]
 * @param {number} [params.value]
 * @param {object[]} [params.items]
 * @param {string} [params.coupon]
 * @param {string} [params.shipping_tier]
 * @param {string} [params.payment_type]
 */
export function pushEcommerceEvent(eventName, params) {
  if (typeof window === 'undefined' || !window.protatDoTracking) {
    return;
  }

  const {
    currency,
    value,
    items,
    coupon,
    shipping_tier: shippingTier,
    payment_type: paymentType,
  } = params;

  window.dataLayer = window.dataLayer || [];

  /** @type {Record<string, unknown>} */
  const ecommerce = {};
  if (currency) {
    ecommerce.currency = currency;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    ecommerce.value = value;
  }
  if (items && items.length) {
    ecommerce.items = items;
  }
  if (coupon) {
    ecommerce.coupon = coupon;
  }
  if (shippingTier) {
    ecommerce.shipping_tier = shippingTier;
  }
  if (paymentType) {
    ecommerce.payment_type = paymentType;
  }

  clearEcommerce();
  window.dataLayer.push({
    event: eventName,
    ecommerce,
  });
}

/**
 * @param {object} li
 * @returns {{ item_id: string, item_name: string, price: number, quantity: number, item_brand?: string }}
 */
export function mapCartLineItem(li) {
  const rawPrice =
    li.salePrice != null && li.salePrice !== ''
      ? li.salePrice
      : li.price != null && li.price !== ''
      ? li.price
      : 0;
  const price = Number(rawPrice);
  const brand = String(
    li.item_brand || li.brand || li.snapshot?.product?.brandTitle || ''
  ).trim();
  let itemName = String(li.description || li.name || '');
  if (
    brand &&
    itemName &&
    !itemName.toLowerCase().includes(brand.toLowerCase())
  ) {
    itemName = `${brand} - ${itemName}`;
  }

  /** @type {{ item_id: string, item_name: string, price: number, quantity: number, item_brand?: string }} */
  const mapped = {
    item_id: String(li.sku || li.purchasableId || li.id || ''),
    item_name: itemName,
    price: Number.isFinite(price) ? price : 0,
    quantity: Number(li.qty != null ? li.qty : 1) || 1,
  };
  if (brand) {
    mapped.item_brand = brand;
  }
  return mapped;
}

function lineItemPurchasableId(li) {
  if (li.purchasableId != null) {
    return String(li.purchasableId);
  }
  if (li.purchasable && li.purchasable.id != null) {
    return String(li.purchasable.id);
  }
  return '';
}

/**
 * @param {object} data - JSON from commerce/cart/update-cart
 * @param {string|number} purchasableId
 * @param {number} qtyAdded
 */
export function pushAddToCartFromResponse(data, purchasableId, qtyAdded) {
  if (!data?.cart?.lineItems?.length) {
    return;
  }
  const pid = String(purchasableId);
  const matches = data.cart.lineItems.filter(
    (li) => lineItemPurchasableId(li) === pid
  );
  const unitSource =
    matches.length > 0
      ? matches[matches.length - 1]
      : data.cart.lineItems[data.cart.lineItems.length - 1];

  if (!unitSource) {
    return;
  }

  const mapped = mapCartLineItem(unitSource);
  const qty = Math.max(1, Number(qtyAdded) || 1);
  const items = [{ ...mapped, quantity: qty }];
  const value = mapped.price * qty;
  const cur = data.cart.paymentCurrency || data.cart.currency || 'AUD';
  const currency = typeof cur === 'string' ? cur : 'AUD';

  pushEcommerceEvent('add_to_cart', {
    currency,
    value,
    items,
  });
}

function storageKey(prefix, cartNumber) {
  return `${prefix}:${cartNumber || ''}`;
}

function pushOnce(key, callback) {
  const stor = window.sessionStorage;
  if (stor.getItem(key)) {
    return;
  }

  callback();
  stor.setItem(key, '1');
}

function getSelectedShippingTier(form) {
  const checked = form.querySelector(
    'input[name="shippingMethodHandle"]:checked'
  );
  if (!checked) {
    return '';
  }

  const label = form.querySelector(`label[for="${checked.id}"]`);
  const labelText =
    label?.querySelector('span:first-child')?.textContent?.trim() ||
    label?.textContent?.trim().replace(/\s+/g, ' ');
  return labelText || checked.value;
}

function getPaymentType(form) {
  const handle = form.querySelector('input[name="gatewayHandle"]')?.value;
  if (handle) {
    return handle;
  }

  const gatewayId = form.querySelector('input[name="gatewayId"]')?.value;
  return gatewayId || '';
}

/**
 * @param {object} config
 * @param {string} [config.cartNumber]
 * @param {string} config.currency
 * @param {number} config.value
 * @param {object[]} config.items
 * @param {string|null} [config.coupon]
 * @param {string|null} [config.shippingMethodHandle]
 * @param {string|null} [config.paymentGatewayHandle]
 * @param {object} config.page
 * @param {boolean} config.page.isCart
 * @param {boolean} config.page.isCheckout
 * @param {boolean} config.page.isComplete
 * @param {number} config.page.currentStep
 * @param {boolean} config.page.hasItems
 * @param {string|null} [config.page.stepName]
 */
export function bootstrapCheckoutAnalytics(config) {
  if (typeof window === 'undefined' || !window.protatDoTracking || !config) {
    return;
  }

  const {
    cartNumber,
    currency,
    value,
    items,
    coupon,
    shippingMethodHandle,
    paymentGatewayHandle,
    page,
  } = config;

  const safeCurrency =
    typeof currency === 'string' && currency.length ? currency : 'AUD';
  const numericValue = typeof value === 'number' ? value : Number(value) || 0;
  const list = Array.isArray(items) ? items : [];

  if (page.isCart && page.hasItems && list.length) {
    pushEcommerceEvent('view_cart', {
      currency: safeCurrency,
      value: numericValue,
      items: list,
    });
  }

  if (!page.isCheckout || page.isComplete) {
    return;
  }

  const cn = cartNumber || 'unknown';

  if (page.currentStep >= 2 && list.length) {
    const k = storageKey('protat_ga4_begin_checkout', cn);
    pushOnce(k, () => {
      pushEcommerceEvent('begin_checkout', {
        currency: safeCurrency,
        value: numericValue,
        items: list,
        coupon: coupon || undefined,
      });
    });
  }

  if (page.stepName === 'shipping' && list.length) {
    const form = document.getElementById('checkout_shipping');
    form?.addEventListener('submit', (event) => {
      if (event.defaultPrevented) {
        return;
      }

      const k = storageKey('protat_ga4_add_shipping', cn);
      const selectedShippingTier = getSelectedShippingTier(form);
      pushOnce(k, () => {
        pushEcommerceEvent('add_shipping_info', {
          currency: safeCurrency,
          value: numericValue,
          items: list,
          coupon: coupon || undefined,
          shipping_tier:
            selectedShippingTier || shippingMethodHandle || undefined,
        });
      });
    });
  }

  if (page.stepName === 'payment' && list.length) {
    document
      .querySelectorAll('form[id^="checkout_payment"]')
      .forEach((form) => {
        form.addEventListener('submit', (event) => {
          if (event.defaultPrevented) {
            return;
          }

          const k = storageKey('protat_ga4_add_payment', cn);
          const selectedPaymentType = getPaymentType(form);
          pushOnce(k, () => {
            pushEcommerceEvent('add_payment_info', {
              currency: safeCurrency,
              value: numericValue,
              items: list,
              coupon: coupon || undefined,
              payment_type:
                selectedPaymentType || paymentGatewayHandle || undefined,
            });
          });
        });
      });
  }
}

/**
 * @param {object} config
 * @param {string} [config.currency]
 * @param {number} config.value
 * @param {object[]} config.items
 */
export function bootstrapViewItem(config) {
  if (typeof window === 'undefined' || !window.protatDoTracking || !config) {
    return;
  }
  const list = Array.isArray(config.items) ? config.items : [];
  if (!list.length) {
    return;
  }
  const safeCurrency =
    typeof config.currency === 'string' && config.currency.length
      ? config.currency
      : 'AUD';
  const value =
    typeof config.value === 'number' ? config.value : Number(config.value) || 0;

  pushEcommerceEvent('view_item', {
    currency: safeCurrency,
    value,
    items: list,
  });
}
